import { Stack, Box, Typography, Tooltip } from '@mui/material';
import { BaseMultiCheckbox } from 'common/components/base-checkbox-group';
import BaseDialog from 'common/components/base-dialog';
import FormProvider from 'common/components/form-provider';
import { colors } from 'common/constants';
import dayjs from 'dayjs';
import React, { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';
import { getCampaignAnalyticsQuestionDetail } from 'services/campaign.service';
import { ICampaignAnalyticsQuestionDetail } from 'types/campaign';
import { IPagination } from 'types/type.global';
import { handleError } from 'utils/helpers';
import StarIcon from '@mui/icons-material/Star';
import VeryDissatisfied from 'assets/images/icons/question-type/question-emoji/Very Dissatisfied.png';
import Dissatisfied from 'assets/images/icons/question-type/question-emoji/Dissatisfied.png';
import Neutral from 'assets/images/icons/question-type/question-emoji/Neutral.png';
import Satisfied from 'assets/images/icons/question-type/question-emoji/Satisfied.png';
import VerySatisfied from 'assets/images/icons/question-type/question-emoji/Very Satisfied.png';
import { QuestionType } from 'pages/admin/campaign-overview/constants';
import { useAppDispatch } from 'common/hook';
import { setIsLoading } from 'slices/loadingSlice';

interface IProps {
    open: boolean;
    onClose: (open: boolean) => void;
    questionId: number;
}

const emojisForFiveStars = [
    VeryDissatisfied,
    Dissatisfied,
    Neutral,
    Satisfied,
    VerySatisfied,
];
const emojisForThreeItems = [VeryDissatisfied, Neutral, VerySatisfied];
const QuestionDetail: React.FC<IProps> = ({ open, onClose, questionId }) => {
    const dispatch = useAppDispatch();
    const campaignId = useParams<{ id: string }>().id;
    const [pagination, setPagination] = useState<IPagination>({
        page: 1,
        per_page: 10,
    });
    const [questionDetail, setQuestionDetail] =
        useState<ICampaignAnalyticsQuestionDetail | null>();

    const methods = useForm({});

    useEffect(() => {
        const fetchQuestionDetail = async () => {
            dispatch(setIsLoading(true));
            try {
                const response = await getCampaignAnalyticsQuestionDetail(
                    Number(campaignId),
                    questionId,
                    pagination
                );
                if (response.success && response.data) {
                    setQuestionDetail((prev) => ({
                        ...response.data,
                        answers: [
                            ...(prev?.answers || []),
                            ...(response.data.answers || []),
                        ],
                    }));
                    dispatch(setIsLoading(true));
                }
            } catch (error) {
                handleError(error);
                dispatch(setIsLoading(false));
            } finally {
                dispatch(setIsLoading(false));
            }
        };

        fetchQuestionDetail();
    }, [questionId, campaignId, pagination, dispatch]);

    useEffect(() => {
        const { kind } = questionDetail?.question.attributes ?? {};
        if (kind === QuestionType.Checkbox) {
            questionDetail?.answers.forEach((item, index) => {
                methods.setValue(
                    `answers.${index}`,
                    item?.attributes?.multi_choices?.map((id) => Number(id))
                );
            });
        }
        if (
            kind === QuestionType.SingleChoice ||
            kind === QuestionType.Dropdown
        ) {
            questionDetail?.answers.forEach((item, index) => {
                methods.setValue(`answers.${index}`, [
                    item?.attributes?.choice,
                ]);
            });
        }
        if (
            kind === QuestionType.TextboxRecommend ||
            kind === QuestionType.TextboxRecommendMultiChoice
        ) {
            questionDetail?.answers.forEach((item, index) => {
                methods.setValue(
                    `answers.${index}`,
                    item?.attributes?.text_recommend?.map((text) => text)
                );
            });
        }
    }, [questionDetail, pagination, methods]);

    const renderImagesWithTitle = (
        imageOptions: { answer_option_id: number; image: string }[]
    ) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'start',
                    justifyContent: 'start',
                }}
            >
                {imageOptions.map((item, index) => (
                    <>
                        Đáp án:{' '}
                        {
                            questionDetail?.question.attributes.answer_options.find(
                                (option) => option.id === item.answer_option_id
                            )?.text
                        }
                        <img
                            key={index}
                            src={item.image}
                            alt={`Ảnh mô tả ${index + 1}`}
                            style={{
                                height: '200px',
                                borderRadius: '8px',
                                marginBottom: '10px',
                            }}
                        />
                    </>
                ))}
            </Box>
        );
    };

    const renderImages = (images: string[]) => {
        return (
            <Box
                sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '10px',
                    alignItems: 'start',
                    justifyContent: 'start',
                }}
            >
                {images.map((item, index) => (
                    <img
                        key={index}
                        src={item}
                        alt={`Ảnh mô tả ${index + 1}`}
                        style={{
                            height: '300px',
                            borderRadius: '8px',
                            marginBottom: '10px',
                        }}
                    />
                ))}
            </Box>
        );
    };

    const renderAnswerMultiAnswerOptions = (
        matrix: { id?: number; rating?: number }[],
        questionType: string
    ) => {
        return (
            <>
                {matrix.map((item, index) => {
                    return (
                        <>
                            Đáp án:{' '}
                            {
                                questionDetail?.question.attributes.answer_options.find(
                                    (option) => option.id === item.id
                                )?.text
                            }
                            <Box
                                key={index}
                                sx={{ display: 'flex', alignItems: 'stretch' }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '18px',
                                        fontWeight: '700',
                                    }}
                                >
                                    {item.rating}
                                </Typography>
                                {questionType === 'score_star_half' ||
                                    (questionType === 'score_star_full' && (
                                        <Box sx={{ ml: 1 }}>
                                            <StarIcon
                                                sx={{
                                                    color: colors.main,
                                                    '&:hover': {
                                                        color: colors.main,
                                                    },
                                                }}
                                            />
                                        </Box>
                                    ))}
                            </Box>
                        </>
                    );
                })}
            </>
        );
    };
    const renderAnswerEmoji = (matrix: { id?: number; rating?: number }[]) => {
        return (
            <>
                {matrix.map((item, index) => {
                    return (
                        <>
                            Đáp án:{' '}
                            {
                                questionDetail?.question?.attributes?.answer_options?.find(
                                    (option) => option.id === item.id
                                )?.text
                            }
                            <Box
                                mt={1}
                                key={index}
                                sx={{ display: 'flex', alignItems: 'stretch' }}
                            >
                                <img
                                    src={
                                        emojisForFiveStars[
                                            (item?.rating ?? 0) - 1
                                        ] ??
                                        emojisForThreeItems[
                                            (item?.rating ?? 0) - 1
                                        ]
                                    }
                                    alt={`Ảnh mô tả ${index + 1}`}
                                    style={{
                                        width: '32px',
                                        height: '32px',
                                        objectFit: 'cover',
                                        borderRadius: '8px',
                                        marginBottom: '10px',
                                    }}
                                />
                            </Box>
                        </>
                    );
                })}
            </>
        );
    };
    const renderAnswerMatrix = (
        matrix:
            | {
                  question?: string;
                  answer?: string[];
              }[]
            | string[][],
        questionType: string
    ) => {
        return (
            <>
                {(questionType === QuestionType.MatrixRadio ||
                    questionType === QuestionType.MatrixCheckbox) &&
                    matrix.map((item, index) => {
                        return (
                            <Box
                                mt={1}
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                }}
                            >
                                {!Array.isArray(item) && item.question && (
                                    <Typography
                                        sx={{
                                            fontSize: '16px',
                                            fontWeight: '600',
                                        }}
                                    >
                                        {item.question}:{' '}
                                        {item.answer?.map((i) => i).join('; ')}
                                    </Typography>
                                )}
                            </Box>
                        );
                    })}
                {questionType === QuestionType.MatrixText &&
                    matrix.map((item, index) => {
                        return (
                            <Box
                                mt={1}
                                key={index}
                                sx={{
                                    display: 'flex',
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: '600',
                                    }}
                                >
                                    {Array.isArray(item) && item[0]}:{' '}
                                    {Array.isArray(item) &&
                                        item
                                            .slice(1)
                                            .filter(
                                                (value) =>
                                                    value && value.trim() !== ''
                                            )
                                            .join('; ')}
                                </Typography>
                            </Box>
                        );
                    })}
            </>
        );
    };

    const renderAnswerRank = (rank: number[]) => {
        return (
            <>
                {rank?.map((item, index) => {
                    return (
                        <>
                            {/* Đáp án:{' '}
                            {
                                questionDetail?.question.attributes.answer_options.find(
                                    (option) => option.id === item.id
                                )?.text
                            } */}
                            <Box
                                key={index}
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 1,
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                        fontWeight: '700',
                                    }}
                                >
                                    {' '}
                                    Vị trí {index + 1}:
                                </Typography>

                                <Typography
                                    sx={{
                                        fontSize: '16px',
                                    }}
                                >
                                    {
                                        questionDetail?.question.attributes.answer_options.find(
                                            (option) => option.id === item
                                        )?.text
                                    }
                                </Typography>
                            </Box>
                        </>
                    );
                })}
            </>
        );
    };

    return (
        <>
            {open && questionDetail?.question.id && (
                <BaseDialog
                    title={`Xem chi tiết “${questionDetail?.question?.attributes?.title ?? ''}”`}
                    onClose={() => onClose(false)}
                    fullWidth={false}
                    maxWidth={false}
                    sxContent={{ width: '978px', padding: '24px' }}
                    open={open}
                >
                    <FormProvider onSubmit={() => undefined} methods={methods}>
                        <Stack spacing={2}>
                            {questionDetail?.answers.map((item, index) => {
                                return (
                                    <Box
                                        key={index}
                                        sx={{
                                            backgroundColor: '#F7F7F6',
                                            padding: '12px 24px',
                                        }}
                                    >
                                        <Box
                                            sx={{
                                                display: 'grid',
                                                gridTemplateColumns: '1fr 1fr',
                                                pb: 1,
                                                borderBottom:
                                                    '1px solid #E0E0E0',
                                            }}
                                        >
                                            <Typography
                                                sx={{
                                                    fontSize: '20px',
                                                    fontWeight: '600',
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    gap: 1,
                                                }}
                                            >
                                                Đáp viên:
                                                <Tooltip
                                                    title={
                                                        item.attributes.email
                                                    }
                                                    arrow
                                                >
                                                    <Typography
                                                        sx={{
                                                            fontSize: '20px',
                                                            display:
                                                                'inline-block',
                                                            whiteSpace:
                                                                'nowrap',
                                                            fontWeight: 700,
                                                            overflow: 'hidden',
                                                            textOverflow:
                                                                item.attributes
                                                                    .email
                                                                    .length > 40
                                                                    ? 'ellipsis'
                                                                    : 'unset',
                                                            width:
                                                                item.attributes
                                                                    .email
                                                                    .length > 40
                                                                    ? '300px'
                                                                    : 'unset',
                                                            cursor: 'pointer',
                                                        }}
                                                    >
                                                        {item.attributes.email}
                                                    </Typography>
                                                </Tooltip>
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textAlign: 'right',
                                                    fontSize: '20px',
                                                    fontWeight: '600',
                                                }}
                                            >
                                                Thời gian trung bình:{' '}
                                                {
                                                    item.attributes
                                                        .question_answer_time
                                                }{' '}
                                                giây
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#0000004D',
                                                }}
                                            >
                                                Mã: {item.attributes.id}
                                            </Typography>
                                            <Typography
                                                sx={{
                                                    textAlign: 'right',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#0000004D',
                                                }}
                                            >
                                                Đã gửi{' '}
                                                {dayjs(
                                                    item.attributes.created_at
                                                ).format('HH:mm')}
                                            </Typography>
                                            <Typography></Typography>
                                            <Typography
                                                sx={{
                                                    textAlign: 'right',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    color: '#0000004D',
                                                }}
                                            >
                                                Trên thiết bị:{' '}
                                                {item.attributes.device_kind?.toUpperCase()}
                                            </Typography>
                                        </Box>
                                        <Box mt={1}>
                                            {[
                                                QuestionType.Textbox,
                                                QuestionType.Date,
                                                QuestionType.Number,
                                            ].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) && (
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                        }}
                                                    >
                                                        Câu trả lời: {''}
                                                        {item.attributes
                                                            .text && (
                                                            <span>
                                                                {
                                                                    item
                                                                        .attributes
                                                                        .text
                                                                }
                                                            </span>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {[QuestionType.Score].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) && (
                                                <Box>
                                                    <Typography
                                                        sx={{
                                                            fontSize: '16px',
                                                            fontWeight: '600',
                                                        }}
                                                    >
                                                        Câu trả lời: {''}
                                                        {item.attributes
                                                            .rating && (
                                                            <span>
                                                                {
                                                                    item
                                                                        ?.attributes
                                                                        ?.rating
                                                                }
                                                            </span>
                                                        )}
                                                    </Typography>
                                                </Box>
                                            )}
                                            {[
                                                QuestionType.Checkbox,
                                                QuestionType.TextboxRecommend,
                                                QuestionType.TextboxRecommendMultiChoice,
                                                QuestionType.SingleChoice,
                                                QuestionType.Dropdown,
                                            ].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) && (
                                                <BaseMultiCheckbox
                                                    key={JSON.stringify(
                                                        methods.watch(
                                                            `answers.${index}`
                                                        )
                                                    )}
                                                    sxCheckbox={{
                                                        '& .Mui-disabled.Mui-checked .MuiSvgIcon-root':
                                                            {
                                                                color: `${colors.main} !important`,
                                                            },
                                                        '& .MuiFormControlLabel-label':
                                                            {
                                                                fontWeight:
                                                                    '500',
                                                            },
                                                        '& .Mui-checked + .MuiFormControlLabel-label':
                                                            {
                                                                color: colors.main,
                                                            },
                                                    }}
                                                    disabled
                                                    name={`answers.${index}`}
                                                    options={
                                                        questionDetail?.question.attributes.answer_options.map(
                                                            (item) => {
                                                                return {
                                                                    label: item.text,
                                                                    value: Number(
                                                                        item.id
                                                                    ),
                                                                };
                                                            }
                                                        ) ?? []
                                                    }
                                                />
                                            )}
                                            {[QuestionType.Image].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) &&
                                                renderImages(
                                                    item.attributes.images
                                                )}
                                            {[
                                                QuestionType.ImageWithTitle,
                                            ].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) &&
                                                renderImagesWithTitle(
                                                    item.attributes
                                                        .images_option
                                                )}
                                            {[
                                                QuestionType.ScoreStarFull,
                                                QuestionType.ScoreStarHalf,
                                                QuestionType.SliderScale,
                                            ].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) &&
                                                renderAnswerMultiAnswerOptions(
                                                    item.attributes.matrix,
                                                    questionDetail?.question
                                                        .attributes.kind
                                                )}
                                            {[QuestionType.Emoji].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) &&
                                                renderAnswerEmoji(
                                                    item.attributes.matrix
                                                )}
                                            {[
                                                QuestionType.MatrixRadio,
                                                QuestionType.MatrixCheckbox,
                                                QuestionType.MatrixText,
                                            ].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) &&
                                                renderAnswerMatrix(
                                                    item?.attributes?.matrix,
                                                    questionDetail?.question
                                                        .attributes.kind
                                                )}
                                            {[QuestionType.Rank].includes(
                                                questionDetail?.question
                                                    .attributes
                                                    .kind as QuestionType
                                            ) &&
                                                renderAnswerRank(
                                                    item?.attributes?.rank
                                                )}
                                        </Box>
                                    </Box>
                                );
                            })}
                        </Stack>
                        {(questionDetail?.pagination?.total_page ?? 0) >
                            pagination.page && (
                            <Typography
                                onClick={() =>
                                    setPagination({
                                        ...pagination,
                                        page: pagination.page + 1,
                                    })
                                }
                                sx={{
                                    fontSize: '16px',
                                    fontWeight: '600',
                                    color: colors.main,
                                    cursor: 'pointer',
                                    textAlign: 'center',
                                    mt: 2,
                                }}
                            >
                                Xem thêm ({pagination.page}/
                                {questionDetail?.pagination?.total_page})
                            </Typography>
                        )}
                    </FormProvider>
                </BaseDialog>
            )}
        </>
    );
};

export default QuestionDetail;
